import type {ReactElement} from 'react';
import {useState} from 'react';
import type {Block, Editor} from 'grapesjs';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {BlockCategoryMap} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {TextBlockPanel} from '@Components/email-maker-block-manager/components/text-block-panel';
import {ImageBlockPanel} from '@Components/email-maker-block-manager/components/image-block-panel';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import {getUniqueString} from '@Utils/string.util';
import {getBlocksFromCategories, getIconFromBlockId} from '@Libraries/email-component-template-library';
import {noop} from '@Utils/general.util';
import {MobilePanelHeader} from '@Components/email-maker-block-manager/components/mobile-panel-header';
import {useIsMobileScreen} from '@Components/email-maker-block-manager/email-maker-block-manager-grapesjs-editor-context';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import styles from './elements-block-panel.module.scss';

interface ElementBlockPanelProps {
  blocksMap: BlockCategoryMap;
  showBackButton?: boolean;
  headerLabel?: string;
  onBackButton?: VoidFunction;
  gjsEditor: Editor;
}

enum Panel {
  ELEMENTS,
  TEXT,
  IMAGE,
}

export function ElementsBlockPanel({blocksMap, showBackButton = false, headerLabel = '', onBackButton = noop, gjsEditor}: ElementBlockPanelProps): ReactElement<any> {
  const BLOCK_CATEGORY = [window.PMW.emails.blocks.CATEGORY_ELEMENTS];
  const [currentPanel, setCurrentPanel] = useState<Panel>(Panel.ELEMENTS);
  const isMobileScreen = useIsMobileScreen();

  const getBlockLabel = (blockId: string): string => {
    switch (blockId) {
      case window.PMW.emails.blocks.ID_TABLE:
        return window.i18next.t('pmwjs_table');
      case window.PMW.emails.blocks.ID_STICKER:
        return window.i18next.t('pmwjs_sticker');
      case window.PMW.emails.blocks.ID_VIDEO:
        return window.i18next.t('pmwjs_video');
      case window.PMW.emails.blocks.ID_BUTTON:
        return window.i18next.t('pmwjs_button');
      case window.PMW.emails.blocks.ID_SOCIAL:
        return window.i18next.t('pmwjs_social_block');
      case window.PMW.emails.blocks.ID_ICONS:
        return window.i18next.t('pmwjs_icon');
      case window.PMW.emails.blocks.ID_DIVIDER:
        return window.i18next.t('pmwjs_divider');
      case window.PMW.emails.blocks.CATEGORY_TEXT:
        return window.i18next.t('pmwjs_text');
      case window.PMW.emails.blocks.CATEGORY_IMAGE:
        return window.i18next.t('pmwjs_image');
      default:
        return 'Block label not found';
    }
  };

  const getBlockDescription = (blockId: string): string => {
    switch (blockId) {
      case window.PMW.emails.blocks.ID_TABLE:
        return window.i18next.t('pmwjs_table_block_description');
      case window.PMW.emails.blocks.ID_STICKER:
        return window.i18next.t('pmwjs_sticker_block_description');
      case window.PMW.emails.blocks.ID_VIDEO:
        return window.i18next.t('pmwjs_video_block_description');
      case window.PMW.emails.blocks.ID_BUTTON:
        return window.i18next.t('pmwjs_button_block_description');
      case window.PMW.emails.blocks.ID_SOCIAL:
        return window.i18next.t('pmwjs_social_block_description');
      case window.PMW.emails.blocks.ID_ICONS:
        return window.i18next.t('pmwjs_icon_block_description');
      case window.PMW.emails.blocks.ID_DIVIDER:
        return window.i18next.t('pmwjs_divider_block_description');
      case window.PMW.emails.blocks.CATEGORY_TEXT:
        return window.i18next.t('pmwjs_text_block_description');
      case window.PMW.emails.blocks.CATEGORY_IMAGE:
        return window.i18next.t('pmwjs_image_block_description');
      default:
        return 'Block description not found';
    }
  };

  const getBlockGA4TrackingParam = (blockId: string): string => {
    switch (blockId) {
      case window.PMW.emails.blocks.ID_TABLE:
        return 'Table';
      case window.PMW.emails.blocks.ID_STICKER:
        return 'Sticker';
      case window.PMW.emails.blocks.ID_VIDEO:
        return 'Video';
      case window.PMW.emails.blocks.ID_BUTTON:
        return 'Button';
      case window.PMW.emails.blocks.ID_SOCIAL:
        return 'Social';
      case window.PMW.emails.blocks.ID_ICONS:
        return 'Icon';
      case window.PMW.emails.blocks.ID_DIVIDER:
        return 'Divider';
      default:
        return 'Unknown block';
    }
  };

  const getBlockVisualComponent = (blockId: string): ReactElement<any> => {
    return (
      <>
        <Icon icon={getIconFromBlockId(blockId)} className={styles.icon} size={IconSize.SIZE_ICON_24} />
        <div className={styles.blockTextContainer}>
          <Text val={getBlockLabel(blockId)} size={TextSize.XSMALL} bold />
          <Text val={getBlockDescription(blockId)} size={TextSize.XXSMALL} />
        </div>
      </>
    );
  };

  const getTextElementBlock = (): ReactElement<any> => {
    return (
      <ClickableDiv
        className={`${styles.subSectionBlock} ${styles.block}`}
        onClick={(): void => {
          setCurrentPanel(Panel.TEXT);
        }}
      >
        {getBlockVisualComponent(window.PMW.emails.blocks.CATEGORY_TEXT)}
      </ClickableDiv>
    );
  };

  const getImageElementBlock = (): ReactElement<any> => {
    return (
      <ClickableDiv
        className={`${styles.subSectionBlock} ${styles.block}`}
        onClick={(): void => {
          setCurrentPanel(Panel.IMAGE);
        }}
      >
        {getBlockVisualComponent(window.PMW.emails.blocks.CATEGORY_IMAGE)}
      </ClickableDiv>
    );
  };

  const getDefaultElementBlocks = (): ReactElement<any> => {
    const elementBlocks: Block[] = getBlocksFromCategories(blocksMap, BLOCK_CATEGORY);

    return (
      <>
        {elementBlocks.map((block) => {
          return (
            <EmailMakerBlock
              block={block}
              extraClasses={styles.block}
              key={getUniqueString()}
              onAddToCanvas={() => {
                trackGA4EventOnDefaultElementAdd(block.getId());
              }}
            >
              {getBlockVisualComponent(block.getId())}
            </EmailMakerBlock>
          );
        })}
      </>
    );
  };

  const getDefaultPanelContent = (): ReactElement<any> => {
    return (
      <div className={styles.listItems}>
        {getMobilePanelHeader()}
        {getTextElementBlock()}
        {getImageElementBlock()}
        {getDefaultElementBlocks()}
      </div>
    );
  };

  const getTextElementPanelContent = (): ReactElement<any> => {
    return (
      <TextBlockPanel
        blocksMap={blocksMap}
        showBackButton
        onBackButton={() => {
          setCurrentPanel(Panel.ELEMENTS);
        }}
        headerLabel={window.i18next.t('pmwjs_text')}
      />
    );
  };

  const getImageElementPanelContent = (): ReactElement<any> => {
    return (
      <ImageBlockPanel
        blocksMap={blocksMap}
        showBackButton
        onBackButton={() => {
          setCurrentPanel(Panel.ELEMENTS);
        }}
        headerLabel={window.i18next.t('pmwjs_image')}
        gjsEditor={gjsEditor}
      />
    );
  };

  const getMobilePanelHeader = (): ReactElement<any> | null => {
    if (!isMobileScreen || !headerLabel) {
      return null;
    }

    return (
      <div className={styles.mobilePanelHeader}>
        <MobilePanelHeader headerLabel={headerLabel} showHandleBar showBackButton={showBackButton} onBackButton={onBackButton} />
      </div>
    );
  };

  const getPanelContent = (): ReactElement<any> => {
    switch (currentPanel) {
      case Panel.TEXT:
        return getTextElementPanelContent();
      case Panel.IMAGE:
        return getImageElementPanelContent();
      case Panel.ELEMENTS:
      default:
        return getDefaultPanelContent();
    }
  };

  const trackGA4EventOnDefaultElementAdd = (blockId: string): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_ADD_ELEMENT, {[GA4EventParamName.TYPE]: getBlockGA4TrackingParam(blockId)});
  };

  return (
    <div className={`${styles.panelContent} ${currentPanel === Panel.TEXT ? styles.textPanel : ''} ${currentPanel === Panel.IMAGE ? styles.imagePanel : ''}`}>
      {getPanelContent()}
    </div>
  );
}
